import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export function ChangePasswordPage() {
  const { player, markPasswordChanged, logout } = useAuth();
  const navigate = useNavigate();
  const forced = player?.must_change_password === true;

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!current) {
      setError('Aktuelles Passwort fehlt');
      return;
    }
    if (next.length < 6) {
      setError('Neues Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    if (next !== confirm) {
      setError('Die beiden neuen Passwörter stimmen nicht überein');
      return;
    }
    if (next === current) {
      setError('Neues Passwort muss sich vom alten unterscheiden');
      return;
    }

    setBusy(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      markPasswordChanged();
      navigate('/', { replace: true });
    } catch (err) {
      const text = axios.isAxiosError(err) && err.response?.data?.error
        ? String(err.response.data.error)
        : 'Wechsel fehlgeschlagen';
      setError(text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <div className="login__emojis" aria-hidden="true">🔐</div>
      <h1 className="login__title">Passwort wechseln</h1>
      <p className="login__subtitle">
        {forced
          ? 'Beim ersten Login musst du dein Passwort ändern.'
          : `Hallo ${player?.name}, neues Passwort setzen.`}
      </p>

      <form className="login__form" onSubmit={submit}>
        <label className="field">
          <span className="field__label">Aktuelles Passwort</span>
          <input
            className="field__input"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={busy}
            required
            autoFocus
          />
        </label>

        <label className="field">
          <span className="field__label">Neues Passwort</span>
          <input
            className="field__input"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            disabled={busy}
            required
            minLength={6}
          />
        </label>

        <label className="field">
          <span className="field__label">Neues Passwort wiederholen</span>
          <input
            className="field__input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy}
            required
            minLength={6}
          />
        </label>

        {error && <p className="login__error" role="alert">{error}</p>}

        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Wechsle …' : 'Passwort ändern'}
        </button>

        {!forced && (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => navigate(-1)}
            disabled={busy}
          >
            Abbrechen
          </button>
        )}
        {forced && (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              logout();
              navigate('/login', { replace: true });
            }}
            disabled={busy}
          >
            Abbrechen & ausloggen
          </button>
        )}
      </form>

      <p className="login__hint">Mindestens 6 Zeichen. Klartext bleibt bei dir.</p>
    </main>
  );
}
